import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { getValuesFromDate, timezone } from "@/utils";

import { PackagesI } from "./formMap";
import { useAppSelector } from "@/hooks";

interface UseRegisterFormProps {
  urlsArguments: string;
  defaultPackageSlug?: string;
  packages: PackagesI[];
  classId?: number | string;
  otherData: { [key: string]: any };
}

export interface FormValuesI {
  name: string;
  mobileCountryCode: string;
  mobile: string;
  email: string;
  duration: number;
  startDate: Date | null;
  packageSlug: string;
  zoneId: number;
  isSingleClassPurchasePage?: boolean;
}

const useRegisterForm = ({
  urlsArguments,
  defaultPackageSlug,
  packages,
  classId,
  otherData,
  isSingleClassPurchasePage,
}: UseRegisterFormProps) => {
  const { countryCodeData } = useAppSelector((state) => state.dashboard);
  const { selectedShyftPassZoneId } = useAppSelector(
    (state) => state.shyftPass
  );

  const formSchema = yup
    .object({
      name: yup
        .string()
        .required("Please enter your name 55")
        .min(3, "Please enter full name"),
      mobile: yup
        .string()
        .required("Please enter a valid phone number")
        .min(8, "Number should be minimum 8 characters")
        .max(14, "Number should be maximum 14 characters"),
      email: yup
        .string()
        .email("Please enter a valid email address")
        .required("Please enter a valid email address"),
      ...(!isSingleClassPurchasePage && {
        zoneId: yup.string().required("Please select your preferred zone"),
      }),
    })
    .required();

  const defaultValues = useMemo(
    () => ({
      name: "",
      mobileCountryCode: countryCodeData?.default?.code || "+91",
      mobile: "",
      email: "",
      duration: 1,
      startDate: new Date(),
      packageSlug: packages?.find((pack) => pack?.metaTagData?.ranking === 0)
        ?.slug,
      zoneId: selectedShyftPassZoneId || 1,
    }),
    []
  );

  const resolver = useMemo(() => yupResolver(formSchema), []);

  const methods = useForm<FormValuesI>({
    resolver,
    defaultValues,
    mode: "onBlur",
  });

  const { reset, getValues, watch, formState } = methods;

  const resetData = () => reset(defaultValues);

  const packageSlug = watch("packageSlug") || defaultPackageSlug;

  const selectedPackage =
    packages?.find((pack) => pack.slug === packageSlug) || packages?.[0];

  const mapData = useCallback(() => {
    const {
      name,
      mobileCountryCode,
      mobile,
      email,

      duration,
      startDate,
      zoneId,
    } = getValues();

    let formattedDate = "";
    if (startDate) {
      const { date, month, year } = getValuesFromDate(startDate);
      formattedDate = `${year}-${month}-${date}`;
    }

    return {
      ...(name && { name: name }),
      ...(mobileCountryCode && {
        phone_prefix: mobileCountryCode,
      }),
      ...(mobile && { mobile: mobile }),
      ...(email && { email: email }),
      ...(zoneId && { zone_id: zoneId }),
      ...(duration && { duration: duration }),
      ...(startDate && { plan_start_date: formattedDate }),
      ...(packageSlug && { package_slug: packageSlug }),
      ...(urlsArguments && { urls_arguments: urlsArguments }),
      ...(classId && { centertiming_id: classId }),
      ...otherData,
      timezone,
    };
  }, [getValues, urlsArguments, packageSlug, classId, otherData]);

  return { methods, mapData, selectedPackage, resetData };
};

export default useRegisterForm;
